import { useEffect } from 'react'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { CameraPreview } from '@capacitor-community/camera-preview'
import { isCameraDialogMounted } from './cameraSessionFlag'

/**
 * Defensive global cleanup for @capacitor-community/camera-preview.
 *
 * The plugin attaches a native UIView/SurfaceView ABOVE the WKWebView /
 * Android WebView (we mount with `toBack: false`). The plugin does NOT
 * auto-tear-down on backgrounding, and iOS in particular can suspend
 * the JS engine before the React unmount cleanup completes — when that
 * happens, the native preview view stays glued to the screen with no
 * mounted JS state owning it.
 *
 * Symptom (reported by TestFlight + Android testers): after sending
 * the app to the background and returning, the middle of the screen
 * becomes unclickable (the leftover preview view intercepts touches)
 * while the top/bottom toolbars still respond.
 *
 * Mount this hook once at the app root. On every resume (and on every
 * pause as belt-and-suspenders) it calls `CameraPreview.stop()` — a
 * no-op when no preview is alive, idempotent when one is, so it can
 * fire safely even when the camera dialog has never been opened this
 * session. The active CameraLiveView, if mounted, has its own
 * appStateChange handler that restarts the preview after this stop —
 * so this hook never breaks the in-camera resume flow.
 */
export function useCameraPreviewGlobalCleanup() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let cancelled = false
    let handle: { remove: () => Promise<void> } | null = null
    App.addListener('appStateChange', async state => {
      // Only fire on resume. On pause iOS may suspend JS mid-call
      // (which is the root cause of the leak in the first place), so
      // a pause-time stop is unreliable. On resume the JS engine is
      // fresh — we can safely tear down any orphaned preview UIView
      // before the user notices it intercepting touches.
      if (!state.isActive) return

      // CRITICAL: skip when the CameraCaptureDialog is mounted. Its
      // own appStateChange handler will run start()+stop() to
      // re-attach the preview — if we ALSO fire stop() here, the two
      // stop Runnables race the dialog's start Runnable on the UI
      // thread and crash the plugin with
      //   NullPointerException: CameraActivity.setRect on a null
      //   object reference  (CameraPreview.java:343)
      // Reproduced from Samsung A16 5G dropbox: builds 20 and 24
      // both crashed here. Skipping when the dialog is alive means
      // only the in-dialog handler fires lifecycle calls, so there's
      // only one start/stop pair on the UI thread queue.
      if (isCameraDialogMounted()) return

      try {
        await CameraPreview.stop()
      } catch {
        // No preview running — expected most of the time.
      }
    }).then(h => {
      if (cancelled) {
        h.remove()
      } else {
        handle = h
      }
    })
    return () => {
      cancelled = true
      handle?.remove()
    }
  }, [])
}
